import React, { useState, useEffect } from 'react';
import { DuplicateEntity } from 'api/bitedb/entities/entities';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

type DuplicateReviewContentProps = {
  duplicates: DuplicateEntity[];
  onDiscard: () => void;
  onReview: (selectedDuplicates?: DuplicateEntity[]) => void;
  isProcessing?: boolean;
};

export const DuplicateReviewContent: React.FC<DuplicateReviewContentProps> = ({
  duplicates,
  onDiscard,
  onReview,
  isProcessing = false,
}) => {
  const [ selectedIndices, setSelectedIndices ] = useState<Set<number>>(new Set());

  useEffect(() => {
    // Select all by default when duplicates change
    const allIndices = new Set(duplicates.map((_, index) => index));
    setSelectedIndices(allIndices);
  }, [ duplicates ]);

  const toggleSelection = (index: number) => {
    const newSelected = new Set(selectedIndices);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedIndices(newSelected);
  };

  const handleSelectAll = () => {
    const allIndices = new Set(duplicates.map((_, index) => index));
    setSelectedIndices(allIndices);
  };

  const handleDeselectAll = () => {
    setSelectedIndices(new Set());
  };

  const handleReview = () => {
    const selectedDuplicates = duplicates.filter((_, index) =>
      selectedIndices.has(index),
    );
    onReview(selectedDuplicates);
  };

  const renderDuplicateKey = (duplicate: DuplicateEntity, index: number) =>
    duplicate.existing_database_entity_id ||
    duplicate.existing_entity_id ||
    `${duplicate.entity?.name ?? 'duplicate'}-${index}`;

  return (
    <div
      className='audit-add-modal-cont-body'
      style={{ paddingTop: '12px', paddingBottom: '12px' }}
    >
      <div className='audit-add-modal-cont-body-frame'>
        <div className='audit-add-modal-cont-body-frame-form'>
          <div className='audit-add-modal-head-cont-supporting'>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <h1>
                {duplicates.length} potential duplicate{duplicates.length > 1 ? 's' : ''} found
              </h1>
              <div style={{ display: 'flex', gap: '12px' }}>
                <span
                  onClick={handleSelectAll}
                  style={{ color: '#4895ef', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
                >
                  Select All
                </span>
                <span
                  onClick={handleDeselectAll}
                  style={{ color: '#4895ef', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
                >
                  Deselect All
                </span>
              </div>
            </div>
            <p>
              These records already exist in this database. Choose whether to discard them or add them to the review queue for manual review.
            </p>
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '16px', width: '100%', display: 'flex', flexDirection: 'column', alignSelf: 'stretch' }}>
            {duplicates.map((duplicate, index) => {
              const key = renderDuplicateKey(duplicate, index);
              const entity = duplicate.entity;
              const isSelected = selectedIndices.has(index);

              return (
                <div
                  key={key}
                  onClick={() => toggleSelection(index)}
                  style={{
                    marginBottom: '12px',
                    padding: '16px',
                    backgroundColor: '#2A2D33',
                    borderRadius: '8px',
                    width: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    minWidth: 0,
                    cursor: 'pointer',
                    border: isSelected ? '1px solid #4895ef' : '1px solid transparent',
                    position: 'relative',
                  }}
                >
                  <div style={{ position: 'absolute', top: '16px', right: '16px' }}>
                    {isSelected ? (
                      <CheckCircleIcon sx={{ color: '#4895ef', fontSize: 24 }} />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{ color: '#85888E', fontSize: 24 }} />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontWeight: '600', marginBottom: '4px', color: '#FFFFFF' }}>{entity?.name || 'Unnamed Entity'}</p>
                    <p style={{ fontSize: '12px', color: '#85888E', marginBottom: '8px' }}>{duplicate.message}</p>
                    <div className='grid-layout' style={{ width: '100%', marginTop: '8px' }}>
                      <div>
                        <p style={{ fontSize: '12px', color: 'var(--card-text)', margin: '4px 0' }}>
                          <span style={{ color: '#85888E' }}>Email:</span> {entity?.email || 'N/A'}
                        </p>
                        <p style={{ fontSize: '12px', color: 'var(--card-text)', margin: '4px 0' }}>
                          <span style={{ color: '#85888E' }}>Phone:</span> {entity?.phone || 'N/A'}
                        </p>
                        <p style={{ fontSize: '12px', color: 'var(--card-text)', margin: '4px 0' }}>
                          <span style={{ color: '#85888E' }}>Country:</span> {entity?.country_of_origin || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p style={{ fontSize: '12px', color: 'var(--card-text)', margin: '4px 0' }}>
                          <span style={{ color: '#85888E' }}>Category:</span> {entity?.category || 'N/A'}
                        </p>
                        <p style={{ fontSize: '12px', color: 'var(--card-text)', margin: '4px 0' }}>
                          <span style={{ color: '#85888E' }}>Subcategory:</span> {entity?.subcategory || 'N/A'}
                        </p>
                        <p style={{ fontSize: '12px', color: 'var(--card-text)', margin: '4px 0' }}>
                          <span style={{ color: '#85888E' }}>Address:</span> {entity?.address || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div
          className='audit-modal-cont-footer'
          style={{ width: '100%' }}
        >
          <div className='audit-modal-cont-footer-content'>
            <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
              <div
                className='audit-modal-cont-footer-content-btn-cancel'
                style={{ width: '100%' }}
                onClick={!isProcessing ? onDiscard : undefined}
              >
                <div className={`audit-modal-cont-footer-content-btn-text ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  Discard
                </div>
              </div>
              <div
                className='audit-modal-cont-footer-content-btn-upload'
                style={{
                  width: '100%',
                  backgroundColor: selectedIndices.size === 0 ? '#2A2D33' : undefined,
                  cursor: selectedIndices.size === 0 ? 'not-allowed' : 'pointer',
                }}
                onClick={!isProcessing && selectedIndices.size > 0 ? handleReview : undefined}
              >
                <div
                  className={`audit-modal-cont-footer-content-btn-text ${
                    isProcessing || selectedIndices.size === 0 ?
                      'opacity-50 cursor-not-allowed' :
                      ''
                  }`}
                  style={{ color: selectedIndices.size === 0 ? '#85888E' : undefined }}
                >
                  {isProcessing ? 'Adding...' : `Add ${selectedIndices.size} to Review Queue`}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

